import { useStore } from '@nanostores/react';
import { Accordion, AccordionItem, AccordionTrigger } from '@radix-ui/react-accordion';
import { type ToolInvocation, type ToolResult } from 'ai';
import { memo, useEffect, useRef } from 'react';
import { ToolCallText } from '~/components/chat/assistant-message/message-parts/ToolCallText';
import { AccordionContent } from '~/components/ui/Accordion';
import { Button } from '~/components/ui/Button';
import { userStore } from '~/lib/stores/user';
import { classNames } from '~/utils/classNames';

type ToolInvocationResult = string | { content: string } | { type: 'text'; text: string }[] | unknown;

function stringifyToolInvocation(toolInvocation: ToolResult<string, any, ToolInvocationResult>) {
  if (typeof toolInvocation.result === 'string') {
    return toolInvocation.result;
  }

  if (Array.isArray(toolInvocation.result)) {
    return toolInvocation.result
      .map((item) => {
        if (typeof item === 'object' && 'type' in item && item.type === 'text') {
          return item.text;
        }

        return JSON.stringify(item, null, 2);
      })
      .join('\n');
  }

  if (toolInvocation.result && typeof toolInvocation.result === 'object' && 'content' in toolInvocation.result) {
    return toolInvocation.result.content;
  }

  return JSON.stringify(toolInvocation.result, null, 2);
}

interface ToolCallWithDetailsProps {
  toolInvocation: ToolInvocation;
}

export const ToolCallWithDetails = memo(({ toolInvocation }: ToolCallWithDetailsProps) => {
  const { state, args } = toolInvocation;
  const featureFlags = useStore(userStore.featureFlags);

  const isDebugMode = !!featureFlags.debugger;
  const hasFailed = state === 'result' && toolInvocation.result.type === 'error' && toolInvocation.result.content;

  const inputString = args ? JSON.stringify(args, null, 2) : 'No input';

  const outputString = hasFailed
    ? stringifyToolInvocation(toolInvocation)
    : state === 'result' && (stringifyToolInvocation(toolInvocation) || 'No output');

  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let hash = '';

    if (toolInvocation.toolName === 'TodoWrite') {
      const content = toolInvocation.args.todos.find((todo: any) => todo.status === 'in_progress')?.content || '';
      hash = btoa(content);
    } else {
      hash = btoa(toolInvocation.toolCallId);
    }

    if (ref.current && window.location.hash === `#${hash}`) {
      ref.current.scrollIntoView();

      if (toolInvocation.toolName !== 'TodoWrite') {
        ref.current.click();
      }
    }
  }, []);

  if (!isDebugMode) {
    return (
      <div id={btoa(toolInvocation.toolCallId)} className="flex items-start gap-2">
        <ToolCallText toolInvocation={toolInvocation} />
        <div className="flex-1"></div>
      </div>
    );
  }

  return (
    <Accordion type="single" collapsible className="grid">
      <AccordionItem value="tool-call" className="group grid">
        <AccordionTrigger asChild>
          <Button
            ref={ref}
            variant="ghost"
            truncate={false}
            size="md"
            id={btoa(toolInvocation.toolCallId)}
            className="flex items-start gap-2 px-0 h-auto group/button hover:bg-transparent scroll-m-t-3 truncate"
          >
            <ToolCallText toolInvocation={toolInvocation} />

            <div className="flex-1"></div>

            <span className="icon i-ph:caret-down-bold mt-1 pointer-events-none opacity-0 group-hover:opacity-100 group-[[data-state=open]]:rotate-180 size-4 shrink-0 transition-[opacity,transform] duration-200" />
          </Button>
        </AccordionTrigger>

        <AccordionContent className="grid gap-3 pt-4">
          <div className="grid gap-1">
            <div className="text-xs font-medium text-bolt-elements-text-secondary">Input:</div>
            <pre className="text-xs border border-bolt-elements-borderColor p-2 rounded-md overflow-x-auto bg-bolt-ds-surface">
              {inputString}
            </pre>
          </div>

          <div className="grid gap-1">
            <div className="text-xs font-medium text-bolt-elements-text-secondary">Output:</div>

            <pre
              className={classNames(
                'text-xs p-2 overflow-x-auto rounded-md whitespace-pre-wrap bg-bolt-elements-background-depth-2 border border-bolt-elements-borderColor bg-bolt-ds-surface max-h-[324px]',
                hasFailed && 'text-bolt-ds-onDangerContainer',
              )}
            >
              {hasFailed && toolInvocation.result.content ? toolInvocation.result.content : outputString}
            </pre>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
});
